import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { 
  Shield, Check, CreditCard, AlertTriangle, FileText, 
  MessageSquare, Globe, Mail, Phone, Star, Zap
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const ScamCheckPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const entityFromUrl = searchParams.get('entity') || searchParams.get('query') || '';
  const [loading, setLoading] = useState(null);
  const [entity, setEntity] = useState('');
  const handlePayment = async (tier) => {
  setLoading(tier);

  try {
    const planMap = {
      instant: 'scam_check_basic',
      advanced: 'scam_check_pro'
    };

    const plan = planMap[tier];

    const response = await fetch('/api/stripe/checkout-with-scan', {

      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
  plan: plan,
  entity: entityFromUrl,
  entityType: 'domain',
  customerEmail: '',
  originUrl: window.location.origin
})
    });

    const data = await response.json();
    console.log('CHECKOUT RESPONSE:', data);

    if (data.url) {
      window.location.href = data.url;
    } else {
      throw new Error('No checkout URL returned');
    }
  } catch (error) {
    console.error('Payment error:', error);
  } finally {
    setLoading(null);
  }
};

  return (
    <div className="min-h-screen bg-[#0a1628] pt-24 pb-16">
      <Helmet>
        <title>Scam Check Service | ActionFraudOne</title>
        <meta name="description" content="Submit suspicious messages, emails, or websites for professional scam analysis. Get a risk assessment from £4.99." />
      </Helmet>

      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="max-w-3xl mx-auto text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#1e40af]/10 border border-[#1e40af]/30 rounded-full mb-6">
            <Shield className="w-4 h-4 text-cyan-400" />
            <span className="text-cyan-400 text-sm font-medium">Professional Scam Analysis</span>
          </div>
          
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Scam Check Service
          </h1>
          <p className="text-gray-400 text-lg">
            Submit suspicious messages, emails, websites, or investment offers for professional analysis. 
            Our fraud intelligence system will analyze your submission and provide a Scam Risk Report.
          </p>
        </div>

        {/* What You Can Submit */}
        <div className="max-w-4xl mx-auto mb-12">
          <h2 className="text-xl font-bold text-white mb-6 text-center">What You Can Submit</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: MessageSquare, label: 'Text Messages' },
              { icon: Mail, label: 'Emails' },
              { icon: Globe, label: 'Websites' },
              { icon: Phone, label: 'Phone Numbers' },
            ].map((item, index) => (
              <div key={index} className="flex flex-col items-center gap-2 p-4 bg-[#0d1d32] border border-[#1e40af]/20 rounded-lg">
                <item.icon className="w-6 h-6 text-cyan-400" />
                <span className="text-gray-300 text-sm text-center">{item.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Pricing Cards - Standardized 2-tier for scam checks */}
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto mb-16">
          
          {/* Tier 1: Instant Scam Check - £4.99 */}
          <Card className="bg-[#0d1d32] border-blue-500/20 hover:border-blue-500/40 transition-all">
            <CardContent className="p-8">
              <div className="flex items-center gap-2 mb-4">
                <Shield className="w-6 h-6 text-blue-400" />
                <h3 className="text-2xl font-bold text-white">Instant Scam Check</h3>
              </div>
              <div className="flex items-baseline gap-1 mb-6">
                <span className="text-4xl font-bold text-blue-400">£4.99</span>
                <span className="text-gray-500">one-time</span>
              </div>
              
              <p className="text-gray-400 mb-6">
                Basic automated fraud risk scan with key indicators and instant results.
              </p>
              
              <ul className="space-y-3 mb-8">
                {[
                  'Fraud Risk Score (0-100)',
                  'Basic Risk Indicators',
                  'AI Analysis Summary',
                  'Instant Results'
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-gray-300">
                    <Check className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              
              <Button 
                onClick={() => handlePayment('instant')}
                disabled={loading === 'instant'}
                className="w-full bg-[#1e40af] hover:bg-[#1e3a8a] text-white font-bold py-6"
                data-testid="scam-check-instant-btn"
              >
                {loading === 'instant' ? (
                  'Processing...'
                ) : (
                  <>
                    <CreditCard className="mr-2" size={18} />
                    Run Instant Check - £4.99
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Tier 2: Advanced Risk Analysis - £9.99 */}
          <Card className="bg-[#0d1d32] border-cyan-500/30 hover:border-cyan-500/50 transition-all relative overflow-hidden">
            <div className="absolute top-4 right-4 bg-cyan-500 text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
              <Star size={12} /> RECOMMENDED
            </div>
            <CardContent className="p-8">
              <div className="flex items-center gap-2 mb-4">
                <Zap className="w-6 h-6 text-cyan-400" />
                <h3 className="text-2xl font-bold text-white">Advanced Risk Analysis</h3>
              </div>
              <div className="flex items-baseline gap-1 mb-6">
                <span className="text-4xl font-bold text-cyan-400">£9.99</span>
                <span className="text-gray-500">one-time</span>
              </div>
              
              <p className="text-gray-400 mb-6">
                Extended fraud intelligence analysis with deeper indicators and recommendations.
              </p>
              
              <ul className="space-y-3 mb-8">
                {[
                  'Everything in Instant Check',
                  'Deep Fraud Indicators',
                  'Pattern Analysis',
                  'Related Scam Reports',
                  'Recommended Actions',
                  'Priority Support'
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-gray-300">
                    <Check className="w-5 h-5 text-cyan-500 flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              
              <Button 
                onClick={() => handlePayment('advanced')}
                disabled={loading === 'advanced'}
                className="w-full bg-gradient-to-r from-[#1e40af] to-cyan-500 hover:from-[#1e3a8a] hover:to-cyan-400 text-white font-bold py-6"
                data-testid="scam-check-advanced-btn"
              >
                {loading === 'advanced' ? (
                  'Processing...'
                ) : (
                  <>
                    <CreditCard className="mr-2" size={18} />
                    Get Advanced Analysis - £9.99
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* How It Works */}
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-white mb-8 text-center">How It Works</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center p-6 bg-[#0d1d32] border border-[#1e40af]/20 rounded-xl">
              <div className="w-12 h-12 bg-[#1e40af]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-cyan-400">1</span>
              </div>
              <h3 className="text-white font-semibold mb-2">Select Package</h3>
              <p className="text-gray-500 text-sm">Choose Instant Check (£4.99) or Advanced Analysis (£9.99)</p>
            </div>
            <div className="text-center p-6 bg-[#0d1d32] border border-[#1e40af]/20 rounded-xl">
              <div className="w-12 h-12 bg-[#1e40af]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-cyan-400">2</span>
              </div>
              <h3 className="text-white font-semibold mb-2">Submit Details</h3>
              <p className="text-gray-500 text-sm">Enter the suspicious website, company, or contact details</p>
            </div>
            <div className="text-center p-6 bg-[#0d1d32] border border-[#1e40af]/20 rounded-xl">
              <div className="w-12 h-12 bg-[#1e40af]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-cyan-400">3</span>
              </div>
              <h3 className="text-white font-semibold mb-2">Get Results</h3>
              <p className="text-gray-500 text-sm">Receive your fraud risk analysis report instantly</p>
            </div>
          </div>
        </div>

        {/* CTA for Higher Tier Services */}
        <div className="max-w-4xl mx-auto mt-16">
          <div className="bg-[#0d1d32] border border-purple-500/30 rounded-xl p-8 text-center">
            <FileText className="w-10 h-10 text-purple-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">Need a Deeper Investigation?</h3>
            <p className="text-gray-400 mb-6">
              For complex cases, our Professional Assessment (£49) and Investigation Dossier (£1,250) 
              provide in-depth manual investigation with legal-ready documentation.
            </p>
            <Button 
              onClick={() => navigate('/investigations')}
              className="bg-purple-600 hover:bg-purple-700 text-white font-bold"
            >
              View Investigation Services
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScamCheckPage;
